import './VariableSelection.scss';
import { KeyCommands, RichEditorVariable } from '../types';
import { KeyHandlerContext } from '../context/KeyHandlerContext';
import { Popover } from '../..';
import { createPortal } from 'react-dom';
import { mod } from '../utils';
import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

interface VariableSelectionProps {
    options: RichEditorVariable[];
    visible: boolean;
    portalRef?: HTMLSpanElement;
    onSelectVariable: (variable: RichEditorVariable) => void;
    onVisibleChange: (visible: boolean) => void;
}

export const VariableSelection = React.forwardRef<HTMLDivElement, VariableSelectionProps>(
    ({ options, portalRef, visible, onSelectVariable, onVisibleChange }, ref) => {
        return portalRef
            ? createPortal(
                  <Popover
                      trigger="click"
                      visible={visible}
                      placement="bottomRight"
                      destroyTooltipOnHide={true}
                      content={<OptionSelection options={options} ref={ref} onSelectVariable={onSelectVariable} />}
                      onVisibleChange={(visible) => onVisibleChange(visible)}>
                      <span style={{ height: '1em' }} />
                  </Popover>,
                  portalRef
              )
            : null;
    }
);
VariableSelection.displayName = 'VariableSelection';

interface OptionSelectionProps {
    options: RichEditorVariable[];
    onSelectVariable: (variable: RichEditorVariable) => void;
}

const OptionSelection = React.forwardRef<HTMLDivElement, OptionSelectionProps>(({ options, onSelectVariable }, ref) => {
    const [selected, setSelected] = useState<number>(0);
    const { register, deregister } = useContext(KeyHandlerContext);
    const numOptions = options.length;
    const currentOption = options[selected];
    const selectedRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const nextId = register(KeyCommands.AutoCompleteNext, () => {
            setSelected((current) => mod(current + 1, numOptions));
            return 'handled';
        });

        const prevId = register(KeyCommands.AutoCompletePrevious, () => {
            setSelected((current) => mod(current - 1, numOptions));
            return 'handled';
        });

        const selectId = register(KeyCommands.AutoCompleteSelect, () => {
            if (currentOption) {
                onSelectVariable(currentOption);
            }
            return 'handled';
        });

        return () => {
            deregister(KeyCommands.AutoCompleteNext, nextId);
            deregister(KeyCommands.AutoCompletePrevious, prevId);
            deregister(KeyCommands.AutoCompleteSelect, selectId);
        };
    }, [register, deregister, numOptions, currentOption, onSelectVariable]);

    useEffect(() => {
        selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selected]);

    return (
        <div className="rain-editor-autocomplete" ref={ref}>
            {options.map((option, index) => {
                return (
                    <div
                        key={option.name.toString()}
                        ref={selected === index ? selectedRef : undefined}
                        className={classnames('rain-editor-autocomplete-option', selected === index && 'selected')}
                        onMouseOver={() => setSelected(index)}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onSelectVariable(option);
                        }}>
                        {option.displayText ?? (Array.isArray(option.name) ? option.name[0] : option.name)}
                    </div>
                );
            })}
        </div>
    );
});
OptionSelection.displayName = 'OptionSelection';
